/**
 * Logger Service with CloudWatch Integration
 * Logs all API calls, errors, and user actions to CloudWatch
 * NO MORE CONSOLE DUMPS - All logs visible in CloudWatch
 */

import { v4 as uuidv4 } from 'uuid';
import { getLoggingConfig, isConfigLoaded, isDevelopment, getApiBaseUrl } from '@/config/runtime-config';
import { appStorage, authStorage } from '@/lib/storage';

// Log Levels
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
  FATAL = 'fatal',
}

// Log Categories
export enum LogCategory {
  API = 'api',
  AUTH = 'auth',
  ERROR = 'error',
  PERFORMANCE = 'performance',
  USER = 'user',
  GENERAL = 'general',
}

// Base Log Entry
export interface LogEntry {
  // Timestamp & Identity
  timestamp: number;
  sessionId: string;
  requestId: string;

  // User Context
  userId?: string;
  userEmail?: string;
  userRole?: string;

  // Log Metadata
  level: LogLevel;
  category: LogCategory;
  message: string;

  // Technical Context
  component?: string;
  action?: string;

  // Browser Context
  userAgent: string;
  viewport: { width: number; height: number };
  url: string;

  // Additional Data
  data?: Record<string, any>;
  error?: ErrorDetails;
}

// Error Details
export interface ErrorDetails {
  name: string;
  message: string;
  stack?: string;
  componentStack?: string;
  cause?: any;
}

// API Log Entry
export interface APILogEntry extends LogEntry {
  category: LogCategory.API;
  request: {
    method: string;
    url: string;
    endpoint: string;
    headers: Record<string, string>;
    body?: any;
    queryParams?: Record<string, string>;
  };
  response?: {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body?: any;
    timing: {
      start: number;
      end: number;
      duration: number;
    };
  };
}

// Auth Event Log
export interface AuthLogEntry extends LogEntry {
  category: LogCategory.AUTH;
  event: 'login' | 'logout' | 'token_refresh' | 'token_expired' | 'unauthorized';
  details: {
    email?: string;
    success: boolean;
    reason?: string;
    tokenExpiry?: number;
    previousSession?: string;
  };
}

// Logger Configuration
interface LoggerConfig {
  apiBaseUrl: string;
  consoleEnabled: boolean;
  batchIntervalMs: number;
  appVersion: string;
  logLevel: LogLevel;
}

/**
 * Logger Service Class
 */
class LoggerService {
  private sessionId: string;
  private config: LoggerConfig;
  private logQueue: LogEntry[] = [];
  private batchInterval: ReturnType<typeof setInterval> | null = null;
  private isFlushingLogs = false;

  constructor() {
    this.sessionId = this.getOrCreateSessionId();

    // Initialize with defaults, will be updated when config loads
    this.config = {
      apiBaseUrl: '',
      consoleEnabled: true,
      batchIntervalMs: 10000,
      appVersion: '2.0.0',
      logLevel: LogLevel.DEBUG,
    };

    // Start batch processor
    this.startBatchProcessor();

    // Send queued logs on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flush());

      // Also flush on visibility change (mobile browsers)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flush();
        }
      });
    }
  }

  /**
   * Initialize logger with runtime config
   */
  public initialize() {
    if (isConfigLoaded()) {
      const loggingConfig = getLoggingConfig();
      this.config.consoleEnabled = isDevelopment();
      this.config.batchIntervalMs = loggingConfig.batchIntervalMs;
      this.config.logLevel = this.parseLogLevel(loggingConfig.level);

      console.log('[Logger] ✅ Initialized with config:', {
        consoleEnabled: this.config.consoleEnabled,
        batchInterval: this.config.batchIntervalMs,
        logLevel: this.config.logLevel,
      });
    }
  }

  private parseLogLevel(level: string): LogLevel {
    const levels: Record<string, LogLevel> = {
      debug: LogLevel.DEBUG,
      info: LogLevel.INFO,
      warn: LogLevel.WARN,
      error: LogLevel.ERROR,
      fatal: LogLevel.FATAL,
    };
    return levels[level.toLowerCase()] || LogLevel.INFO;
  }

  private getOrCreateSessionId(): string {
    if (typeof window === 'undefined') return uuidv4();

    let sessionId = sessionStorage.getItem('logging_session_id');
    if (!sessionId) {
      sessionId = uuidv4();
      sessionStorage.setItem('logging_session_id', sessionId);
    }
    return sessionId;
  }

  private startBatchProcessor() {
    this.batchInterval = setInterval(() => {
      if (this.logQueue.length > 0) {
        this.flush();
      }
    }, this.config.batchIntervalMs);
  }

  /**
   * Main logging method
   */
  public async log(entry: Partial<LogEntry>): Promise<void> {
    // Check log level filtering
    if (entry.level && !this.shouldLog(entry.level)) {
      return;
    }

    const fullEntry: LogEntry = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      requestId: entry.requestId || uuidv4(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      viewport:
        typeof window !== 'undefined'
          ? {
              width: window.innerWidth,
              height: window.innerHeight,
            }
          : { width: 0, height: 0 },
      url: typeof window !== 'undefined' ? window.location.href : '',
      level: entry.level || LogLevel.INFO,
      category: entry.category || LogCategory.GENERAL,
      message: entry.message || '',
      ...entry,
    };

    // Add user context if available
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      fullEntry.userId = currentUser.username;
      fullEntry.userEmail = currentUser.email;
      fullEntry.userRole = currentUser.groups?.[0];
    }

    // Console output (development only)
    if (this.config.consoleEnabled) {
      this.logToConsole(fullEntry);
    }

    // Add to queue for batch processing
    this.logQueue.push(fullEntry);

    // Immediate flush for errors and fatal logs
    if (entry.level === LogLevel.ERROR || entry.level === LogLevel.FATAL) {
      await this.flush();
    }
  }

  private shouldLog(level: LogLevel): boolean {
    const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR, LogLevel.FATAL];
    const configLevelIndex = levels.indexOf(this.config.logLevel);
    const entryLevelIndex = levels.indexOf(level);
    return entryLevelIndex >= configLevelIndex;
  }

  private logToConsole(entry: LogEntry) {
    const styles: Record<LogLevel, string> = {
      [LogLevel.DEBUG]: 'color: #999',
      [LogLevel.INFO]: 'color: #0066cc',
      [LogLevel.WARN]: 'color: #ff9900; font-weight: bold',
      [LogLevel.ERROR]: 'color: #cc0000; font-weight: bold',
      [LogLevel.FATAL]:
        'background: #cc0000; color: white; font-weight: bold; padding: 2px 4px',
    };

    const style = styles[entry.level];
    const prefix = `[${entry.level.toUpperCase()}] ${entry.category}`;

    console.log(`%c${prefix} - ${entry.message}`, style, {
      ...entry,
      // Redact sensitive data in console
      data: this.redactSensitiveData(entry.data),
    });
  }

  /**
   * Flush logs to CloudWatch
   */
  public async flush(): Promise<void> {
    if (this.logQueue.length === 0 || this.isFlushingLogs) {
      return;
    }

    this.isFlushingLogs = true;
    const batch = [...this.logQueue];
    this.logQueue = [];

    try {
      // Get API base URL from config
      if (!isConfigLoaded()) {
        // Config not loaded yet, store logs for later
        this.storeLogsLocally(batch);
        return;
      }

      const apiBaseUrl = getApiBaseUrl();

      /**
       * ⚠️ INTENTIONAL EXCEPTION: Logger uses direct fetch() instead of apiClient
       *
       * WHY: To avoid circular dependency
       * - apiClient logs every request/response using logger
       * - If logger used apiClient, it would create infinite loop
       * - logger.log() → apiClient.post() → logger.log() → apiClient.post() → ∞
       *
       * TRADE-OFFS ACCEPTED:
       * - ✅ No automatic token refresh (logging endpoint is public/minimal auth)
       * - ✅ No request ID propagation (logs already have their own IDs)
       * - ✅ No automatic retry (logs are best-effort delivery)
       * - ✅ No logging of logging calls (would be redundant/circular)
       *
       * This is a documented architectural decision for Golden Eagle sprint.
       */
      // Backend accepts max 50 entries per batch — chunk if needed
      const chunks: LogEntry[][] = [];
      for (let i = 0; i < batch.length; i += 50) {
        chunks.push(batch.slice(i, i + 50));
      }

      const currentUser = this.getCurrentUser();

      for (const chunk of chunks) {
        const res = await fetch(`${apiBaseUrl}/system/logs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            logs: chunk.map(entry => ({
              timestamp: entry.timestamp,
              level: entry.level.toUpperCase(),
              category: entry.category,
              message: entry.message,
              sessionId: entry.sessionId,
              requestId: entry.requestId,
              data: entry.data,
            })),
            metadata: {
              environment: 'dev',
              appVersion: this.config.appVersion,
              sessionId: this.sessionId,
              userId: currentUser?.username,
              userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
            },
          }),
          keepalive: true,
          credentials: 'omit',
        });
        if (!res.ok) {
          throw new Error(`Failed to send logs: ${res.status}`);
        }
      }

      if (this.config.consoleEnabled) {
        console.log(`[Logger] ✅ Sent ${batch.length} logs to CloudWatch`);
      }
    } catch {
      // /system/logs endpoint not yet available — silently store locally (P7)
      this.storeLogsLocally(batch);
    } finally {
      this.isFlushingLogs = false;
    }
  }

  private storeLogsLocally(logs: LogEntry[]) {
    if (typeof localStorage === 'undefined') return;

    try {
      const failedLogs: LogEntry[] = appStorage.get<LogEntry[]>('failed_logs') || [];
      failedLogs.push(...logs);

      // Keep only last 100 logs to avoid filling localStorage
      const trimmed = failedLogs.slice(-100);
      appStorage.set('failed_logs', trimmed);
    } catch (e) {
      console.error('[Logger] Failed to store logs locally:', e);
    }
  }

  private getCurrentUser() {
    if (typeof localStorage === 'undefined') return null;
    return authStorage.get('current_user');
  }

  /**
   * Recursively redact sensitive data from objects
   * Handles: passwords, tokens, API keys, PII, credit cards, etc.
   */
  private redactSensitiveData(data: any): any {
    if (!data) return data;

    // Handle primitives
    if (typeof data !== 'object') return data;

    // Handle arrays recursively
    if (Array.isArray(data)) {
      return data.map(item => this.redactSensitiveData(item));
    }

    const redacted: any = {};

    for (const key of Object.keys(data)) {
      const lowerKey = key.toLowerCase();
      const value = data[key];

      // Full redaction for highly sensitive fields
      if (
        lowerKey.includes('password') ||
        lowerKey.includes('secret') ||
        lowerKey.includes('apikey') ||
        lowerKey.includes('api_key') ||
        lowerKey.includes('privatekey') ||
        lowerKey.includes('private_key') ||
        lowerKey === 'ssn' ||
        lowerKey === 'socialsecurity' ||
        lowerKey.includes('creditcard') ||
        lowerKey.includes('cardnumber') ||
        lowerKey === 'cvv' ||
        lowerKey === 'cvc' ||
        lowerKey.includes('authorization') ||
        lowerKey.includes('auth_token')
      ) {
        redacted[key] = '[REDACTED]';
        continue;
      }

      // Token redaction with partial visibility
      if (
        lowerKey.includes('token') &&
        typeof value === 'string' &&
        value.length > 20
      ) {
        // Show first 10 chars for debugging, rest redacted
        redacted[key] = `${value.substring(0, 10)}...[REDACTED]`;
        continue;
      }

      // Email masking (keep first 2 chars and domain)
      if (
        lowerKey.includes('email') &&
        typeof value === 'string' &&
        value.includes('@')
      ) {
        const [localPart, domain] = value.split('@');
        if (localPart.length > 2) {
          redacted[key] = `${localPart.substring(0, 2)}***@${domain}`;
        } else {
          redacted[key] = `***@${domain}`;
        }
        continue;
      }

      // Phone number masking (show last 4 digits)
      if (
        (lowerKey.includes('phone') || lowerKey.includes('mobile')) &&
        typeof value === 'string' &&
        value.length > 4
      ) {
        const lastFour = value.slice(-4);
        redacted[key] = `***${lastFour}`;
        continue;
      }

      // Recursively process nested objects
      if (typeof value === 'object' && value !== null) {
        redacted[key] = this.redactSensitiveData(value);
        continue;
      }

      // Keep non-sensitive data as-is
      redacted[key] = value;
    }

    return redacted;
  }

  // Convenience methods
  public debug(message: string, data?: any, component?: string) {
    return this.log({
      level: LogLevel.DEBUG,
      category: LogCategory.GENERAL,
      message,
      data,
      component,
    });
  }

  public info(message: string, data?: any, component?: string) {
    return this.log({
      level: LogLevel.INFO,
      category: LogCategory.GENERAL,
      message,
      data,
      component,
    });
  }

  public warn(message: string, data?: any, component?: string) {
    return this.log({
      level: LogLevel.WARN,
      category: LogCategory.GENERAL,
      message,
      data,
      component,
    });
  }

  public error(message: string, error?: Error, data?: any, component?: string) {
    return this.log({
      level: LogLevel.ERROR,
      category: LogCategory.ERROR,
      message,
      error: error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack || '',
            cause: (error as any).cause,
          }
        : undefined,
      data,
      component,
    });
  }

  public fatal(message: string, error?: Error, data?: any, component?: string) {
    return this.log({
      level: LogLevel.FATAL,
      category: LogCategory.ERROR,
      message,
      error: error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack || '',
            cause: (error as any).cause,
          }
        : undefined,
      data,
      component,
    });
  }

  /**
   * Destroy logger and cleanup
   */
  public destroy() {
    if (this.batchInterval) {
      clearInterval(this.batchInterval);
      this.batchInterval = null;
    }
    this.flush();
  }
}

// Singleton instance
export const logger = new LoggerService();

// Export for cleanup
if (typeof window !== 'undefined') {
  (window as any).__logger = logger;
}
